/**
 * `<note-file:PATH>` lines in a transcript, turned back into the files they name.
 *
 * These tags are what this app puts in an agent's input when you attach something, so
 * they come back in the transcript as part of the message you sent. Rendered as prose
 * they are a wall of absolute path — the one thing the user already knows, in place of
 * the picture they actually sent.
 *
 * THE ID IS THE FILENAME, by construction rather than by convention: the server names
 * every blob `<file_id><.ext>` and says why (`note_files.py:105`) — with the original
 * name out of the path, charset, separators, length limits, NFC/NFD and case-collisions
 * all stop being askable. So the id can be read back off the path, and `/notes/file/blob`
 * takes it.
 *
 * That is a derivation across a seam, so it is made deliberately narrow. A path is only
 * treated as a note file when it sits in the note-files directory AND its basename is
 * exactly 32 hex characters with an optional extension. Anything else stays text: a
 * path we cannot resolve is still information, and inventing a blob URL for it would
 * produce a broken image where a readable line used to be.
 *
 * Strictness here is also the security half. The server's own note: a filename like
 * `evil.png>` has extension `png>` — "exactly the injection the tag is vulnerable to".
 * The capture therefore stops at the first `>` and the id is matched against a fixed
 * alphabet, so nothing from the tag reaches a URL unchecked.
 */

/** Where the server stores note blobs. Home differs per machine; these tails do not.
 *  BOTH generations — the portless edge writes under ~/.avengents/, v1 wrote under
 *  ~/.agent-api/ — because transcripts hold tags from both eras, and recognising only
 *  the old one is why every image attached since the migration rendered as an inert
 *  chip (owner, 2026-08-08: 讓 Clean View 可以解析並且繪製圖案標籤的圖案). */
const NOTE_DIRS = ['/.avengents/note-files/', '/.agent-api/note-files/']

/** `uuid4().hex` — 32 hex characters, and never anything else. */
const ID = /^([0-9a-f]{32})(?:\.([a-z0-9]+))?$/

/** Extensions worth showing inline. Everything else is offered as a link instead. */
const IMAGE_EXT = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'avif'])

export type NoteFileRef = {
  /** The whole tag as it appeared, so a caller can strip exactly what it matched. */
  tag: string
  path: string
  /** Set only when the path resolves to a blob this client can fetch. */
  fileId?: string
  isImage: boolean
  /** Last path segment, for the cases where there is nothing better to show. */
  name: string
}

/** `[^>\n]+` — stop at the first `>` and never cross a line. */
const TAG = /<note-file:([^>\n]+)>/g

export function parseNoteFileTags(text: string): { text: string; files: NoteFileRef[] } {
  const files: NoteFileRef[] = []
  const stripped = text.replace(TAG, (tag, raw: string) => {
    const path = raw.trim()
    const name = path.split('/').filter(Boolean).pop() ?? path
    const m = NOTE_DIRS.some((d) => path.includes(d)) ? ID.exec(name) : null
    // Image-ness is the EXTENSION's fact, not the directory's: an agent shares a
    // screenshot from anywhere on disk, and the tag names it. Only the blob-store
    // fileId derivation stays narrow — for an arbitrary path the renderer fetches
    // by path instead, and a non-image extension stays a readable line.
    const ext = (name.match(/\.([a-z0-9]+)$/i)?.[1] ?? '').toLowerCase()
    files.push({
      tag,
      path,
      ...(m ? { fileId: m[1]! } : {}),
      isImage: IMAGE_EXT.has(ext),
      name,
    })
    return ''
  })
  // Collapse the blank lines the tags leave behind. They were written on their own
  // lines, and removing the text without the newlines leaves a gap that reads as a
  // missing paragraph.
  return { text: stripped.replace(/\n{3,}/g, '\n\n').trim(), files }
}
